interface FlattenedTranslations {
    [language: string]: {
        [key: string]: string;
    };
}

/**
 * Flattens a nested translation object into a structure where each language
 * has a flat dictionary of translation messages with full path keys.
 */
export function flattenTranslations(obj: Record<string, any>): FlattenedTranslations {
    const result: FlattenedTranslations = {};

    // Helper function to check if a node is a language node with messages
    function isLanguageNode(key: string, value: any): boolean {
        const isLanguageCode = /^[a-z]{2}(-[A-Z]{2})?$/.test(key) || key === 'default';
        
        if (!isLanguageCode || !value || typeof value !== 'object') return false;
        
        // Format avec "messages"
        if ('messages' in value && typeof value.messages === 'object') {
            return true;
        }
        
        // Format avec "default"
        if ('default' in value && typeof value.default === 'object') {
            return true;
        }
        
        // Vérifier si le nœud contient directement des traductions
        if (Object.values(value).every(val => typeof val === 'string')) {
            return true;
        }
        
        return false;
    }

    // Helper function to determine if a node might contain language submodules
    function mightContainLanguageSubmodules(value: any): boolean {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

        // Check if any direct child is a language node
        return Object.entries(value).some(([childKey, childValue]) =>
            isLanguageNode(childKey, childValue)
        );
    }

    // Process each top-level key separately to skip it in the path
    for (const [topLevelKey, topLevelValue] of Object.entries(obj)) {
        // Skip non-objects
        if (!topLevelValue || typeof topLevelValue !== 'object') continue;

        function traverse(node: Record<string, any>, path: string[] = []) {
            // Skip non-objects or null
            if (!node || typeof node !== 'object' || Array.isArray(node)) return;

            // Check if current node contains language entries
            const potentialLanguages = Object.entries(node);
            const hasLanguageNodes = potentialLanguages.some(
                ([key, value]) => isLanguageNode(key, value)
            );

            if (hasLanguageNodes) {
                // Process all language nodes in this container
                for (const [lang, langValue] of potentialLanguages) {
                    if (isLanguageNode(lang, langValue)) {
                        // Ensure language exists in result
                        if (!result[lang]) {
                            result[lang] = {};
                        }

                        // Build the path prefix
                        const prefix = path.join('.');

                        // Add translations from "messages" format
                        if ('messages' in langValue && typeof langValue.messages === 'object') {
                            for (const [msgKey, msgValue] of Object.entries(langValue.messages)) {
                                if (typeof msgValue === 'string') {
                                    const fullKey = prefix ? `${prefix}.${msgKey}` : msgKey;
                                    result[lang][fullKey] = msgValue;
                                } else if (msgValue && typeof msgValue === 'object') {
                                    // Récursion pour les sous-objets dans messages
                                    traverse(
                                        { [msgKey]: msgValue },
                                        prefix ? [...path, msgKey] : [msgKey]
                                    );
                                }
                            }
                        }
                        
                        // Add translations from "default" format
                        if ('default' in langValue && typeof langValue.default === 'object') {
                            for (const [msgKey, msgValue] of Object.entries(langValue.default)) {
                                if (typeof msgValue === 'string') {
                                    const fullKey = prefix ? `${prefix}.${msgKey}` : msgKey;
                                    result[lang][fullKey] = msgValue;
                                } else if (msgValue && typeof msgValue === 'object') {
                                    // Récursion pour les sous-objets dans default
                                    traverse(
                                        { [msgKey]: msgValue },
                                        prefix ? [...path, msgKey] : [msgKey]
                                    );
                                }
                            }
                        }
                        
                        // Add direct translations
                        if (Object.values(langValue).every(val => typeof val === 'string')) {
                            for (const [msgKey, msgValue] of Object.entries(langValue)) {
                                const fullKey = prefix ? `${prefix}.${msgKey}` : msgKey;
                                result[lang][fullKey] = msgValue as string;
                            }
                        }

                        // Check for language submodules
                        for (const [subKey, subValue] of Object.entries(langValue)) {
                            if (mightContainLanguageSubmodules(subValue)) {
                                traverse(subValue, [...path, subKey]);
                            }
                        }
                    }
                }

                // Check for mixed content (both language nodes and regular nodes)
                for (const [key, value] of potentialLanguages) {
                    if (!isLanguageNode(key, value) && typeof value === 'object' && value) {
                        traverse(value, [...path, key]);
                    }
                }
            } else {
                // Traverse all keys if this is not a language container
                for (const [key, value] of Object.entries(node)) {
                    traverse(value, [...path, key]);
                }
            }
        }

        // Start traversal at second level without including first level in path
        for (const [secondLevelKey, secondLevelValue] of Object.entries(topLevelValue)) {
            traverse(secondLevelValue, [secondLevelKey]);
        }
    }

    return result;
}